import { PaginatedResponse } from '@gorila-shared-ui/components';
import { MemoExoticComponent, ReactElement } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList as List, ListChildComponentProps } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import { SetterOrUpdater } from 'recoil';
import { DEFAULT_LIST_ITEM_HEIGHT } from '../../constants/app';
import { useStyles } from '../../hooks/useStyles';

type Props<T> = {
  ids?: string[];
  loading: boolean;
  onNextPage: SetterOrUpdater<number>;
  itemComponent: MemoExoticComponent<
    ({
      data,
      index,
      style,
    }: ListChildComponentProps<{
      items: any;
      isLoaded: (index: number) => boolean;
    }>) => ReactElement
  >;
  paginatedResponse?: PaginatedResponse<T>;
  itemSize?: number;
};

export function VirtualizedPaginatedList<T>({
  ids,
  loading,
  paginatedResponse,
  onNextPage,
  itemComponent,
  itemSize = DEFAULT_LIST_ITEM_HEIGHT,
}: Readonly<Props<T>>) {
  const { css } = useStyles();
  const itemCount = paginatedResponse?.hasNext ? (ids?.length ?? 0) + 1 : (ids?.length ?? 0);
  const isItemLoaded = (index: number) => !paginatedResponse?.hasNext || index < (ids?.length ?? 0);
  const loadMoreItems =
    loading || !paginatedResponse?.hasNext || !paginatedResponse
      ? () => {}
      : () => {
          onNextPage((prev) => prev + 1);
        };

  if (!ids) return null;

  return (
    <ul
      className={css({
        margin: 0,
        paddingLeft: 0,
        paddingRight: 0,
        height: '100%',
        flex: 1,
      })}
    >
      <AutoSizer>
        {({ width, height }) => (
          <InfiniteLoader
            isItemLoaded={isItemLoaded}
            itemCount={itemCount}
            loadMoreItems={loadMoreItems}
          >
            {({ onItemsRendered, ref }) => (
              <List
                height={height ?? 500}
                itemCount={itemCount}
                itemSize={() => itemSize}
                width={width ?? 200}
                itemData={{
                  items: ids,
                  isLoaded: isItemLoaded,
                }}
                onItemsRendered={onItemsRendered}
                overscanCount={4}
                ref={ref}
              >
                {itemComponent}
              </List>
            )}
          </InfiniteLoader>
        )}
      </AutoSizer>
    </ul>
  );
}
